export type FindReplaceColumnScope = 'all' | 'column';
export type FindReplaceRowScope = 'all' | 'visible' | 'selected';

export interface FindReplaceOptions {
  caseSensitive: boolean;
  wholeCell: boolean;
  scope: FindReplaceColumnScope;
  scopeColumn?: string;
  useRegularExpression?: boolean;
  rowScope?: FindReplaceRowScope;
  rowIndices?: number[];
}

export interface SearchMatch {
  row: number;
  column: string;
  value: string;
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getRegexFlags = (options: FindReplaceOptions, replaceAll: boolean): string =>
  `${options.caseSensitive ? '' : 'i'}${replaceAll ? 'g' : ''}`;

const buildRegexSource = (searchTerm: string, options: FindReplaceOptions): string =>
  options.useRegularExpression ? searchTerm : escapeRegExp(searchTerm);

const buildFindReplaceRegex = (
  searchTerm: string,
  options: FindReplaceOptions,
  replaceAll: boolean
): RegExp => {
  const source = buildRegexSource(searchTerm, options);
  const anchoredSource = options.wholeCell ? `^(?:${source})$` : source;
  return new RegExp(anchoredSource, getRegexFlags(options, replaceAll && !options.wholeCell));
};

export const getFindReplaceRegexError = (
  searchTerm: string,
  options: FindReplaceOptions
): string | null => {
  if (!options.useRegularExpression || searchTerm.trim().length === 0) {
    return null;
  }

  try {
    buildFindReplaceRegex(searchTerm, options, false);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid regular expression';
  }
};

export const isFindReplaceValueMatch = (
  value: string,
  searchTerm: string,
  options: FindReplaceOptions
): boolean => {
  if (searchTerm.trim().length === 0 || getFindReplaceRegexError(searchTerm, options)) {
    return false;
  }

  if (options.useRegularExpression) {
    return buildFindReplaceRegex(searchTerm, options, false).test(value);
  }

  const candidate = options.caseSensitive ? value : value.toLowerCase();
  const needle = options.caseSensitive ? searchTerm : searchTerm.toLowerCase();
  return options.wholeCell ? candidate === needle : candidate.includes(needle);
};

export const buildFindReplaceValue = (
  value: string,
  searchTerm: string,
  replaceValue: string,
  options: FindReplaceOptions
): string => {
  if (!isFindReplaceValueMatch(value, searchTerm, options)) {
    return value;
  }

  if (options.useRegularExpression) {
    return value.replace(buildFindReplaceRegex(searchTerm, options, true), replaceValue);
  }

  if (options.wholeCell) {
    return replaceValue;
  }

  return value.replace(buildFindReplaceRegex(searchTerm, options, true), replaceValue);
};

export const resolveFindReplaceRowIndices = (
  rowCount: number,
  options: FindReplaceOptions,
  selectedRowIndices: number[] = []
): number[] | null => {
  const rowScope = options.rowScope ?? 'all';
  if (rowScope === 'all') {
    return null;
  }

  const sourceRows = rowScope === 'selected'
    ? (selectedRowIndices.length > 0 ? selectedRowIndices : options.rowIndices ?? [])
    : options.rowIndices ?? [];

  return Array.from(
    new Set(
      sourceRows.filter(
        (rowIndex) =>
          Number.isInteger(rowIndex) &&
          rowIndex >= 0 &&
          rowIndex < rowCount
      )
    )
  ).sort((left, right) => left - right);
};
